import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Skeleton, Alert, Button } from "antd";
import PropTypes from "prop-types";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import chatwidgetService from "../../services/chatwidget.service";

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

const DocumentPreviewer = ({ documentId, visible, selectedPages }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const maxRetries = 3;

  const currentBlobUrlRef = useRef(null);

  const documentOptions = useMemo(() => ({
    cMapUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/cmaps/",
    cMapPacked: true,
    standardFontDataUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/standard_fonts/",
  }), []);

  useEffect(() => {
    let isActive = true;

    const fetchPdf = async () => {
      if (!documentId) return;

      try {
        if (currentBlobUrlRef.current) {
          URL.revokeObjectURL(currentBlobUrlRef.current);
          currentBlobUrlRef.current = null;
        }

        const blob = await chatwidgetService.getDocument(documentId);
        const newBlobUrl = URL.createObjectURL(blob);
        currentBlobUrlRef.current = newBlobUrl;

        if (isActive) {
          setPdfUrl(newBlobUrl);
          setLoadError(null);
        }
      } catch (err) {
        console.error("PDF fetch error:", err);
        if (isActive) {
          setLoadError(err.message || "Failed to load PDF");
          setPdfUrl(null);
        }
      }
    };

    fetchPdf();

    return () => {
      isActive = false;
      if (currentBlobUrlRef.current) {
        URL.revokeObjectURL(currentBlobUrlRef.current);
        currentBlobUrlRef.current = null;
      }
    };
  }, [documentId, retryCount]);

  useEffect(() => {
    setNumPages(null);
    setLoadError(null);
    setRetryCount(0);
    setCurrentPage(1);
  }, [documentId]);

  const onLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setLoadError(null);
  }, []);

  const onLoadError = useCallback((error) => {
    console.error("PDF load error:", error);
    if (retryCount < maxRetries) {
      setRetryCount((prev) => prev + 1);
    } else {
      setLoadError(error.message || "Failed to load PDF after multiple attempts");
    }
  }, [retryCount]);

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, numPages));
  };

  const shouldRenderAllPages = !selectedPages || selectedPages.length === 0;
  const pageWidth = Math.min(window.innerWidth * 0.9, 800);

  if (!visible) return null;

  if (!pdfUrl) {
    return <Alert message="Loading document..." type="info" />;
  }

  if (loadError) {
    return (
      <Alert message="Error loading PDF" description={loadError} type="error" />
    );
  }

  return (
    <div className="document-previewer-container" style={{ textAlign: "center" }}>
      <Document
        file={pdfUrl}
        onLoadSuccess={onLoadSuccess}
        onLoadError={onLoadError}
        loading={<Skeleton active />}
        options={documentOptions}
      >
        {numPages &&
          (shouldRenderAllPages ? (
            Array.from({ length: numPages }, (_, i) => (
              <Page
                key={`page_${i + 1}`}
                pageNumber={i + 1}
                width={pageWidth}
                loading={<Skeleton active />}
                renderTextLayer={false}
              />
            ))
          ) : (
            <Page
              key={`page_${currentPage}`}
              pageNumber={currentPage}
              width={pageWidth}
              loading={<Skeleton active />}
              renderTextLayer={false}
            />
          ))}
      </Document>

      {!shouldRenderAllPages && numPages > 1 && (
        <div style={{ marginTop: 16 }}>
          <Button onClick={handlePrevious} disabled={currentPage <= 1}>
            Previous
          </Button>
          <span style={{ margin: "0 12px" }}>
            Page {currentPage} of {numPages}
          </span>
          <Button onClick={handleNext} disabled={currentPage >= numPages}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

DocumentPreviewer.propTypes = {
  documentId: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  selectedPages: PropTypes.arrayOf(PropTypes.number),
};

DocumentPreviewer.defaultProps = {
  selectedPages: [],
};

export default DocumentPreviewer;
